import { Component } from '@theme/component';
import { getBundleStore } from '@theme/pdp-bundle-store';

/**
 * @typedef {object} CrossSellRefs
 * @property {HTMLUListElement} [list] - UL listing cross-sell rows
 * @property {HTMLInputElement[]} [checkboxes] - Per-variant checkboxes
 */

/**
 * PDP block that lets the shopper tick extra variants to add to the cart
 * alongside the main product. Each ticked checkbox publishes the selection
 * to the bundle store; on add-to-cart the store collects all contributors
 * (bands, cross-sell, subscription...) into a single batched /cart/add.json
 * call sharing the same `_bundle_key`.
 *
 * @extends Component<CrossSellRefs>
 */
class AxCrossSell extends Component {
  /** @type {(() => void) | null} */
  #unregisterContributor = null;

  connectedCallback() {
    super.connectedCallback();
    this.#registerWithStore();
    document.addEventListener('model:update', this.#onModelUpdate);
    this.addEventListener('change', this.#onSelectionChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregisterContributor?.();
    this.#unregisterContributor = null;
    document.removeEventListener('model:update', this.#onModelUpdate);
    this.removeEventListener('change', this.#onSelectionChange);
  }

  #onModelUpdate = () => {
    this.#resetSelection();
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });
  };

  /** @param {Event} event */
  #onSelectionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.classList.contains('cross-sell-block__checkbox')) return;
    getBundleStore().publish('cross-sell');
  };

  #registerWithStore() {
    const store = getBundleStore();
    store.setContext({
      productId: this.dataset.productId ?? '',
      sectionId: this.dataset.sectionId ?? '',
    });
    this.#unregisterContributor = store.register({
      name: 'cross-sell',
      order: 20,
      getItems: (ctx) =>
        this.#getSelection().map((sel) => ({
          id: sel.id,
          quantity: 1,
          properties: {
            _bundle_role: 'xsell',
            _bundle_parent_variant: ctx.mainVariantId,
          },
        })),
      getDisplay: () =>
        this.#getCheckboxes()
          .filter((input) => input.checked)
          .map((input) => ({
            role: 'xsell',
            title: input.dataset.variantTitle ?? '',
            priceHtml: input.dataset.variantPriceHtml ?? '',
            priceCents: Number(input.dataset.variantPrice) || 0,
            imageUrl: input.dataset.variantImage ?? '',
            variantId: input.dataset.variantId ?? '',
          })),
      onModelUpdate: () => {
        // Reset is owned by the class-level #onModelUpdate handler (#resetSelection).
        // The contributor itself doesn't hold extra state.
      },
      onCommit: () => {
        // After a successful add-to-cart batch, uncheck all boxes.
        this.#resetSelection();
      },
    });
  }

  /** @returns {HTMLInputElement[]} */
  #getCheckboxes() {
    // Query DOM directly — refs may be stale right after a model-picker morph
    // (MutationObserver updates refs in a microtask, order vs our handler is
    // not guaranteed). See .claude/rules/pdp-morph.md.
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.cross-sell-block__checkbox'))
    );
  }

  #resetSelection() {
    for (const input of this.#getCheckboxes()) {
      input.checked = false;
    }
    getBundleStore().publish('cross-sell');
  }

  /**
   * @returns {Array<{ id: number, variantId: string }>}
   */
  #getSelection() {
    const selection = [];
    for (const input of this.#getCheckboxes()) {
      if (!input.checked) continue;
      const variantId = input.dataset.variantId;
      if (!variantId) continue;
      selection.push({ id: Number(variantId), variantId });
    }
    return selection;
  }

}

if (!customElements.get('ax-cross-sell')) {
  customElements.define('ax-cross-sell', AxCrossSell);
}
